import { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import {
  NumberInput,
  Button,
  Tabs,
  Switch,
  Space,
  TextInput,
  Group,
  Stack,
  Text,
  Box,
  Checkbox,
  Table,
} from '@mantine/core';
import { axios } from '../../../../utils/axios';
import MantineTable from '../../../../common/table/mantine';
import { useParams } from 'react-router-dom';
import { IconRefresh } from '@tabler/icons-react';
import { CostingRequestsContext, useCrStatusCancelled } from '../../../../contexts/CostingRequest';
import { createStyles } from '@mantine/core';
import ContentPaper from '../../../../components/layouts/rfs/Content';
import styled from '@emotion/styled';
import { showAlert } from '../../../../utils/alerts';
import { ProjectOverview } from '../projectOverview';
import { checkDiff, checkIfFlagged, formatFloat, transformData,AddChangeNumberPopup } from './helper';
import { AppLoader } from '../../../../components/common/loader';
import { NotificationModal } from '../../../../components/modal/NotificationModal';
import { RefBadge } from './refBadge';
import { notificationPopup } from '../../../../constants';
import moment from 'moment';

const FooterContainer = styled.div`
  float: right;
  button {
    width: 200px;
  }
`;

const useStyles = createStyles((theme) => ({
  input: {
    outline: '2px solid black',
    outlineOffset: '-1px',
  },
  thumb: {
    color: 'black',
    backgroundColor: 'black',
  },
  track: {
    border: '3px solid black',
    margin: '1px',
  },
}));

export const NormsCheck = ({ activeTab, setLogsModal }) => {
  const { classes } = useStyles();
  const isCancelled = useCrStatusCancelled();
  const { crNumber } = useParams();
  const { info, isCrRoleAuthorized } = useContext(CostingRequestsContext);
  const [loading, setLoading] = useState(false);
  const [baseSku, setBaseSku] = useState([]);
  const [switchState, setSwitchState] = useState(false);
  const [activePlant, setActivePlant] = useState();
  const { producing_plant_data = [] } = info || {};
  const [normStatus, setNormStatus] = useState();
  const [btnDisable, setBtnDisable] = useState(false);
  const [errors, setErrors] = useState({});
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [refGroup, setRefGroup] = useState('');
  const [refGroupCounter, setRefGroupCounter] = useState('');
  const [mainGroup, setMainGroup] = useState(null);
  const [mainGroupCounter, setMainGroupCounter] = useState(null);
  // const [confirmed, setConfirmed] = useState(false);
  const [opened, setOpened] = useState(false);
  const material = info?.sku_data?.[0]?.sku;
  useEffect(() => {
    if (activeTab === 'normAdjustment') {
      const hasAccess = isCrRoleAuthorized(String(activePlant));
      hasAccess ? fetch(true) : fetch(false);
      setActivePlant(producing_plant_data[0]?.producing_plant);
    }
  }, [activeTab]);
  useEffect(() => {
    if (activeTab === 'normAdjustment') {
      handleSubmitStatus(normStatus, activePlant);
    }
    getRecipeGroup(producing_plant_data[0]?.producing_plant);
  }, []);

  const fetch = async (refresh = false, plant = activePlant) => {
    if (!plant) plant = producing_plant_data[0]?.producing_plant;
    if (refresh) {
      try {
        await axios.post(`/costing-request/step/norms/refresh/${crNumber}/?plant=${plant}`, {});
      } catch (e) {}
    }
    try {
      setLoading(true);

      const response = await axios.get(`/costing-request/step/norms/${crNumber}`);
      const apiData = response.data?.data;
      setNormStatus(response?.data?.norm_status);
      handleSubmitStatus(response?.data?.norm_status, plant);
      const updatedData = transformData(apiData);
      setBaseSku(updatedData);
    } catch (err) {
      console.log('err : normsCheck.js', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitStatus = (step, plant = activePlant) => {
    if (Array.isArray(step) && step.length > 0) {
      const activePlantId = producing_plant_data.filter(
        (ele) => ele.producing_plant === String(plant),
      )?.[0]?.id;
      const stepsData = step.filter((ele) => ele.producing_plant_id === activePlantId);
      if (Array.isArray(stepsData)) {
        (stepsData?.[0]?.status === 'In Progress' || stepsData?.[0]?.is_submission_allowed) &&
        isCrRoleAuthorized(String(plant))
          ? setBtnDisable(false)
          : setBtnDisable(true);
      }
      return;
    }
  };

  const queueApiCalls = async (skus, batchSize) => {
    try {
      let queue = [...skus];
      let results = [];
      let error = [];

      const executeBatch = async () => {
        const currentBatch = queue.splice(0, batchSize);
        if (currentBatch.length > 0) {
          const dataToSave = currentBatch.filter(
            (item) => item.itemModified && item.producingPlant.producing_plant === activePlant,
          );
          const responses = await Promise.allSettled(
            dataToSave.map(async (prevData) => {
              try {
                return await handleSaveChange(prevData);
              } catch (err) {
                error.push(prevData.component_description);
                throw err;
              }
            }),
          );

          results.push(...responses);

          // Continue processing the next batch
          await executeBatch();
        }
      };

      await executeBatch();
      return { results, error };
    } catch (error) {
      throw error;
    }
  };

  const handleAutoAdjustment = () => {
    setBaseSku((prevData) =>
      prevData.map((ele) => {
        if (
          ele.reference_bom?.[0]?.quantity &&
          formatFloat(ele.quantity) !== formatFloat(ele.reference_bom?.[0]?.quantity)
        ) {
          ele.quantity = ele.reference_bom?.[0]?.quantity;
          ele.itemModified = true;
        }
        if (
          ele.reference_bom?.[0]?.activity_type &&
          ele.activity_type !== ele.reference_bom?.[0].activity_type
        ) {
          ele.activity_type = ele.reference_bom?.[0].activity_type;
          ele.itemModified = true;
        }
        if (
          ele.reference_bom?.[0]?.charge_quantity &&
          formatFloat(ele.charge_quantity) !== formatFloat(ele.reference_bom?.[0].charge_quantity)
        ) {
          ele.charge_quantity = ele.reference_bom?.[0].charge_quantity;
          ele.itemModified = true;
        }
        if (
          ele.reference_bom?.[0]?.operation_quantity &&
          formatFloat(ele.operation_quantity) !==
            formatFloat(ele.reference_bom?.[0].operation_quantity)
        ) {
          ele.operation_quantity = ele.reference_bom?.[0].operation_quantity;
          ele.itemModified = true;
        }
        ele.flagged = false;
        return ele;
      }),
    );
  };
  const handleSaveChange = (selectedSku) => {
    return new Promise((resolve, reject) => {
      const payload = {
        component_id: selectedSku.component_id,
        quantity: selectedSku.quantity,
        operation_quantity: selectedSku.operation_quantity,
        activity_type: selectedSku.activity_type,
        charge_quantity: selectedSku.charge_quantity,
        comment: '',
      };

      axios
        .post(`/costing-request/step/norms/${crNumber}`, payload)
        .then((res) => {
          resolve(res);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  const handleSubmit = async () => {
    setNotificationModalOpen(true);
    setLoading(true);
    try {
      const { error } = await queueApiCalls(baseSku, 1);

      if (error.length > 0) {
        setErrors((o) => {
          return {
            ...o,
            [activePlant]: error,
          };
        });
      }

      const payload = {
        plant: activePlant,
      };
      axios
        .post(`/costing-request/step/norms/submit/${crNumber}`, payload)
        .then((res) => {
          if (res?.data?.success && error.length === 0) {
            showAlert({ message: 'Successfully Submitted' }, 'success');
          } else {
            showAlert({ message: `Failed to save ${error.join(', ')}` }, 'error');
          }
        })
        .catch((error) => {
          showAlert({ message: error?.response?.data?.error }, 'error');
        })
        .finally(() => {
          fetch(true);
        });
    } catch (e) {
      showAlert(e, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = useCallback((id, field, value, data) => {
    setBaseSku((prevData) =>
      prevData.map((ele) => {
        if (ele?.component_id === id) {
          ele[field] = value;
          ele['flagged'] = checkIfFlagged(ele, ele?.reference_bom?.[0]);
          ele.itemModified = true;
        }
        return ele;
      }),
    );
  }, []);

  const fetchLogs = async () => {
    try {
      const url = `/costing-request/step/norms/submit/logs/${crNumber}`;
      const response = await axios.get(url);
      if (response.status !== 200) {
        throw new Error('Failed to fetch logs');
      }
      return response.data.data;
    } catch (error) {
      console.error('Error fetching logs:', error);
      throw error;
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'component_description',
        header: 'Norm Name',
        index: 0,
      },
      {
        accessorKey: 'component',
        header: 'Norm Resource',
      },
      {
        id: 'duration_excel',
        accessorFn: (row) => row,
        header: 'Duration',
        Cell: ({ row }) => {
          const { diff, variance } = checkDiff(
            row?.original?.quantity,
            row?.original?.reference_bom?.[0]?.quantity,
          );
          return (
            <Table style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <th>Main</th>
                  <th>Ref</th>
                  <th>Var</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <NumberInput
                        disabled={btnDisable || isCancelled}
                        precision={3}
                        min={0}
                        step={0.01}
                        value={parseFloat(row.original?.quantity || '') || 0}
                        onChange={(val) =>
                          handleInputChange(
                            row.original?.component_id ?? '',
                            'quantity',
                            val,
                            row.original,
                          )
                        }
                        styles={(theme) => ({
                          input: {
                            borderColor: diff ? 'red' : theme.colors.gray[5],
                          },
                        })}
                      />
                    </div>
                  </td>
                  <td>
                    {row?.original?.hasReference && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <TextInput
                          value={parseFloat(row.original?.reference_bom?.[0]?.quantity) || 0}
                          readOnly
                          disabled
                        />
                      </div>
                    )}
                  </td>
                  <td>
                    {row?.original?.hasReference && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginTop: '3px' }}>
                          <span style={parseInt(variance) === 0 ? {} : { color: 'red' }}>
                            {Math.abs(variance)}
                          </span>
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          );
        },
        size: 350,
      },
      {
        id: 'unit_excel',
        accessorFn: (row) => row,
        header: 'Unit',
        size: 50,
        Cell: ({ row }) => {
          return (
            <>
              <div style={{ display: 'flex', paddingTop: '40px' }}>
                <span key={row.original?.sku_id || 'key-1'} style={{ margin: '8px' }}>
                  {row.original?.component_unit}
                </span>
              </div>
            </>
          );
        },
      },
      {
        id: 'activity_type_excel',
        accessorFn: (row) => row,
        header: 'Activity Type',
        grow: false,
        size: 250,
        Cell: ({ row }) => {
          const isRefActivityTypeThere =
            row.original?.activity_type !== '' && row.original?.reference_bom?.[0]?.activity_type
              ? true
              : false;
          const isDiff = isRefActivityTypeThere
            ? (row.original?.activity_type || '').trim() !==
              (row.original?.reference_bom?.[0]?.activity_type || '').trim()
            : false;
          return (
            <Table style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <th>Main</th>
                  <th>Ref</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <TextInput
                        disabled={btnDisable || isCancelled || row.original?.activity_type !== ''}
                        value={row.original?.activity_type || ''}
                        onChange={(event) => {
                          handleInputChange(
                            row.original?.component_id ?? '',
                            'activity_type',
                            event.target.value.toLocaleUpperCase(),
                            row.original,
                          );
                        }}
                        styles={(theme) => ({
                          input: {
                            borderColor: isDiff ? 'red' : theme.colors.gray[5],
                          },
                        })}
                      />
                    </div>
                  </td>

                  <td>
                    {row?.original?.hasReference && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <TextInput
                          readOnly
                          disabled
                          value={row.original?.reference_bom?.[0]?.activity_type || ''}
                        />
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          );
        },
      },
      {
        id: 'charge_qty_excel',
        accessorFn: (row) => row,
        size: 300,
        header: 'Charge Qty',
        Cell: ({ row }) => {
          const { diff, variance } = checkDiff(
            row?.original?.charge_quantity,
            row?.original?.reference_bom?.[0]?.charge_quantity,
          );
          return (
            <Table style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <th>Main</th>
                  <th>Ref</th>
                  <th>Var</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <NumberInput
                        disabled={btnDisable || isCancelled}
                        precision={3}
                        min={0}
                        step={0.01}
                        max={1000000}
                        value={parseFloat(row.original?.charge_quantity || '') || 0}
                        onChange={(val) =>
                          handleInputChange(
                            row.original?.component_id ?? '',
                            'charge_quantity',
                            val,
                            row.original,
                          )
                        }
                        styles={(theme) => ({
                          input: {
                            borderColor: diff ? 'red' : theme.colors.gray[5],
                          },
                        })}
                      />
                    </div>
                  </td>

                  <td>
                    {row?.original?.hasReference && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {'\xa0\xa0'}
                        <NumberInput
                          readOnly
                          min={0}
                          value={parseInt(row.original?.reference_bom?.[0]?.charge_quantity) || 0}
                          disabled
                        />
                      </div>
                    )}
                  </td>

                  <td>
                    {row?.original?.hasReference && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={parseInt(variance) === 0 ? {} : { color: 'red' }}>
                          {Math.abs(variance)}
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          );
        },
      },
      {
        id: 'charge_qty_unit_excel',
        accessorFn: (row) => row,
        header: 'C Unit',
        size: 150,
        Cell: ({ row }) => {
          return (
            <Table style={{ tableLayout: 'fixed', width: '100%'}}>
              <thead>
                <tr>
                  <th>Main</th>
                  <th>Ref</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {row.original?.charge_quantity_unit}
                 
                    </div>
                  </td>
                  <td>
                    {row?.original?.hasReference && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '8px' }}>
                          {row.original?.reference_bom?.[0]?.charge_quantity_unit || ' '}
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          );
        },
      },
      {
        id: 'operation_qty_excel',
        accessorFn: (row) => row,
        header: 'Operation Qty',
        grow: false,
        size: 250,
        Cell: ({ row }) => {
          const { diff, variance } = checkDiff(
            row?.original?.operation_quantity,
            row?.original?.reference_bom?.[0]?.operation_quantity,
          );
          return (
            <Table style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <th>Main</th>
                  <th>Ref</th>
                  <th>Var</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <NumberInput
                        disabled={btnDisable || isCancelled}
                        precision={3}
                        min={0}
                        step={0.01}
                        max={1000000}
                        value={parseFloat(row.original?.operation_quantity || '') || 0}
                        onChange={(val) =>
                          handleInputChange(
                            row.original?.component_id ?? '',
                            'operation_quantity',
                            val,
                            row.original,
                          )
                        }
                        styles={(theme) => ({
                          input: {
                            borderColor: diff ? 'red' : theme.colors.gray[5],
                            borderWidth: '1px',
                            margin: '0px',
                          },
                        })}
                      />
                    </div>
                  </td>

                  <td>
                    {row?.original?.hasReference && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <NumberInput
                          precision={0}
                          min={0}
                          value={
                            parseInt(row.original?.reference_bom?.[0]?.operation_quantity || '') ||
                            0
                          }
                          readOnly
                          disabled
                        />
                      </div>
                    )}
                  </td>

                  <td>
                    {row?.original?.hasReference && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={parseInt(variance) === 0 ? {} : { color: 'red' }}>
                          {Math.abs(variance)}
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          );
        },
      },
      {
        id: 'operation_qty_unit_excel',
        accessorFn: (row) => row,
        header: 'OQ Unit',
        grow: false,
        size: 100,
        Cell: ({ row }) => {
          return (
            <Table style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <th>Main</th>
                  <th>Ref</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ marginRight: '8px' }}>
                        {row.original?.operation_quantity_unit}
                      </span>
                    </div>
                  </td>
                  <td>
                    {row?.original?.hasReference && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '8px' }}>
                          {row.original?.reference_bom?.[0]?.operation_quantity_unit || ' '}
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          );
        },
      },
      {
        id: 'total_value_excel',
        accessorFn: (row) => row,
        header: 'Total Value (per HL)',
        size: 300,

        Cell: ({ row }) => {
          const reference = row.original?.reference_bom?.[0]?.PrimaryRefSkuCostEstimation || null;
          const { diff, variance } = checkDiff(
            row.original?.RequestedSkuCostEstimation?.total_value,
            reference?.total_value || 0,
          );
          return (
            <Table style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <th>Main</th>
                  <th>Ref</th>
                  <th>Var</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <NumberInput
                        precision={3}
                        min={0}
                        step={0.01}
                        value={
                          parseFloat(row.original?.RequestedSkuCostEstimation?.total_value || '') ||
                          0
                        }
                        disabled
                        styles={(theme) => ({
                          input: {
                            borderColor: diff ? 'red' : theme.colors.gray[4],
                          },
                        })}
                      />
                    </div>
                  </td>

                  <td>
                    {row.original.hasReference && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <NumberInput
                          precision={3}
                          min={0}
                          step={0.01}
                          disabled
                          readOnly
                          value={parseFloat(reference?.total_value || '0') ?? 0}
                          styles={(theme) => ({
                            input: {
                              width: '100px',
                            },
                          })}
                        />
                      </div>
                    )}
                  </td>

                  <td>
                    {row?.original?.hasReference && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={parseInt(variance) === 0 ? {} : { color: 'red' }}>
                          {Math.abs(variance)}
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          );
        },
      },
      {
        id: 'price_excel',
        accessorFn: (row) => row,
        header: 'Price',
        size: 150,
        Cell: ({ row }) => {
          const reference = row.original?.reference_bom?.[0]?.PrimaryRefSkuCostEstimation || null;
          return (
            <Table style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <th>Main</th>
                  <th>Ref</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ marginRight: '8px' }}>
                        {row.original?.RequestedSkuCostEstimation?.price || ''}
                      </span>
                    </div>
                  </td>

                  <td>
                    {row?.original?.hasReference && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        {reference?.Price || reference?.price || 0}
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          );
        },
      },
      {
        id: 'price_unit_excel',
        accessorFn: (row) => row,
        header: 'Price Unit \xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0',
        size: 150,
        Cell: ({ row }) => {
          return (
            <Table style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <th>Main</th>
                  <th>Ref</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ marginRight: '8px' }}>
                        {row.original?.RequestedSkuCostEstimation?.un || ''}
                      </span>
                    </div>
                  </td>

                  <td>
                    {row?.original?.hasReference && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '8px' }}>
                          {row.original?.reference_bom?.[0]?.PrimaryRefSkuCostEstimation?.un || ''}
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          );
        },
      },
      {
        id: 'price_per_unit_excel',
        accessorFn: (row) => row,
        header: 'Price Per Unit  \xa0\xa0\xa0\xa0\xa0',

        size: 150,
        Cell: ({ row }) => {
          return (
            <Table style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <tr>
                  <th>Main</th>
                  <th>Ref</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ marginRight: '8px' }}>
                        {row.original?.RequestedSkuCostEstimation?.price_unit || ''}
                      </span>
                    </div>
                  </td>

                  <td>
                    {row?.original?.hasReference && (
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '8px' }}>
                          {row.original?.reference_bom?.[0]?.PrimaryRefSkuCostEstimation
                            ?.price_unit || ''}
                        </span>
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          );
        },
      },
    ],
    [handleInputChange, baseSku, errors],
  );

  const getExportData = () => {
    let dataToExport = [];

    const skus = baseSku.filter((item) => item?.producingPlant?.producing_plant === activePlant);

    skus.forEach((data) => {
      const {
        component_description,
        component,
        quantity,
        reference_bom,
        component_unit,
        activity_type,
        charge_quantity,
        charge_quantity_unit,
        operation_quantity,
        operation_quantity_unit,
        RequestedSkuCostEstimation,
      } = data;
      const { variance: durationVariance } = checkDiff(quantity, reference_bom?.[0]?.quantity);

      const { variance: chargeQuantityVariance } = checkDiff(
        charge_quantity,
        reference_bom?.[0]?.charge_quantity,
      );

      const { variance: operationQtyVariance } = checkDiff(
        operation_quantity,
        reference_bom?.[0]?.operation_quantity,
      );

      const { variance: totalValueVariance } = checkDiff(
        RequestedSkuCostEstimation?.total_value,
        reference_bom?.[0]?.PrimaryRefSkuCostEstimation?.total_value || 0,
      );

      dataToExport.push({
        'Norm Name': component_description,
        'Norm Resource': component,
        'Duration Main': quantity,
        'Duration Ref': reference_bom?.[0]?.quantity,
        'Duration Var': Math.abs(parseFloat(durationVariance)),
        'Unit Main': component_unit,
        'Unit Ref': reference_bom?.[0]?.component_unit,
        'Activity Type Main': activity_type,
        'Activity Type Ref': reference_bom?.[0]?.activity_type,
        'Charge Qty Main': charge_quantity,
        'Charge Qty Ref': reference_bom?.[0]?.charge_quantity,
        'Charge Qty Var': Math.abs(parseFloat(chargeQuantityVariance)),
        'C Unit Main': charge_quantity_unit,
        'C Unit Ref': reference_bom?.[0]?.charge_quantity_unit,
        'Operation Qty Main': operation_quantity,
        'Operation Qty Ref': reference_bom?.[0]?.operation_quantity,
        'Operation Qty Var': Math.abs(parseFloat(operationQtyVariance)),
        'OQ Unit Main': operation_quantity_unit,
        'OQ Unit Ref': reference_bom?.[0]?.operation_quantity_unit,
        'Total Value (per HL) Main': RequestedSkuCostEstimation?.total_value,
        'Total Value (per HL) Ref': reference_bom?.[0]?.PrimaryRefSkuCostEstimation?.total_value,
        'Total Value (per HL) Var': Math.abs(parseFloat(totalValueVariance)),
        'Price Main': RequestedSkuCostEstimation?.price,
        'Price Ref':
          reference_bom?.[0]?.PrimaryRefSkuCostEstimation?.Price ||
          reference_bom?.[0]?.PrimaryRefSkuCostEstimation?.price ||
          0,
        'Price Unit Main': RequestedSkuCostEstimation?.un,
        'Price Unit Ref': reference_bom?.[0]?.PrimaryRefSkuCostEstimation?.un,
        'Price Per Unit Main': RequestedSkuCostEstimation?.price_unit,
        'Price Per Unit Ref': reference_bom?.[0]?.PrimaryRefSkuCostEstimation?.price_unit,
      });
    });

    dataToExport = dataToExport.map((row) => {
      const sanitizedRow = {};
      for (const key in row) {
        sanitizedRow[key] = row[key] ?? '';
      }
      return sanitizedRow;
    });

    return dataToExport;
  };

  const rightSideBtns = [
    {
      button: (
        <Button
          color='dark'
          disabled={btnDisable || isCancelled}
          onClick={() => setOpened(true)}
        >
         Change Number
        </Button>
      ),
    },
    {
      button: (
        <Button
          color='dark'
          onClick={async () => {
            try {
              const logs = await fetchLogs();
              setLogsModal({
                open: true,
                logs: logs,
                type: 'NORMS Adjustment Check',
              });
            } catch (error) {
              console.error('Error fetching logs:', error);
            }
          }}
        >
          View Logs
        </Button>
      ),
    },
    {
      button: (
        <Button
          disabled={btnDisable || isCancelled}
          variant='light'
          color='gray.6'
          onClick={() => fetch(true)}
        >
          <IconRefresh color='black' variant='filled' size={16} />
        </Button>
      ),
    },
    {
      button: (
        <Button
          variant='light'
          size='sm'
          color='blue.3'
          disabled={btnDisable || isCancelled}
          styles={(theme) => ({
            label: {
              color: theme.colors.blue[6],
            },
          })}
          onClick={() => handleAutoAdjustment()}
        >
          Auto Adjustment of BOM
        </Button>
      ),
    },
  ];
  const leftButtons = [
    {
      button: (
        <Switch
          label='View Flagged Data'
          onChange={(event) => {
            setSwitchState(event.target.checked);
          }}
          classNames={{ input: classes.input, thumb: classes.thumb, track: classes.track }}
        />
      ),
    },
  ];
  const initialState = {
    columnPinning: {
      left: ['component_description', 'component'],
      right: [],
    },
    sorting: [
      {
        id: 'component_description',
        desc: false,
      },
    ],
  };
  const getRecipeGroup = async (plant) => {
    try {
      setLoading(true);
      let payload = {
        Plant: plant,
      };

      //response for reference group and group counter
      const resRef = await axios.post(
        `/costing-request/step/norms/fetch-recipe-group-counter/${crNumber}`,
        payload,
      );

      if (resRef.data?.data?.length > 0) {
        const { Group, GroupCounter } = resRef.data.data[0];
        setRefGroup(Group);
        setRefGroupCounter(GroupCounter);
      } else {
        console.warn('No group data found for Reference');
      }
      payload = {
        Material: material,
        Plant: plant,
      };
      const resMain = await axios.post(
        `/costing-request/step/norms/fetch-recipe-group-counter/${crNumber}`,
        payload,
      );

      if (resMain.data?.data?.length > 0) {
        const { Group, GroupCounter } = resMain.data.data[0];
        setMainGroup(Group);
        setMainGroupCounter(GroupCounter);
      } else {
        console.warn('No group data found for Main Sku and Plant');
      }
    } catch (err) {
      console.error('Error fetching recipe group counter:', err.resRef?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (flag) => {
    if (flag) setMainGroup(refGroup);
    else setMainGroupCounter(refGroupCounter);
  };

  // Update backend
  const handleUpdate = async ({ producing_plant: plant, id }) => {
    try {
      setLoading(true);
      const material_data = {
        Plant: plant,
        Material: material,
      };
      const payload = {
        material_data,
        id,
        RecipeGroupCounter: {
          Group: mainGroup,
          GroupCounter: mainGroupCounter,
        },
      };
      const res = await axios.post(
        `/costing-request/step/norms/update-recipe-group-counter/${crNumber}`,
        payload,
      );
      const response = res?.data?.data?.Response;

      if (response.length > 0) {
        response.forEach((item) => {
          item.Success === '1'
            ? showAlert({ message: item.Message }, 'success')
            : showAlert(
                { message: item.Message },
                item.Message === 'Missing task list/material ass' ? 'warning' : 'error',
              );
        });
      } else {
        showAlert({ message: `Failed to Update ` }, 'error');
      }
    } catch (err) {
      console.error('Update error:', err);
      showAlert({ message: `Error Failed to Update ` }, 'error');
    } finally {
      fetch(true);
      setLoading(false);
    }
  };

  const RecipeGroupCounter = ({ producing_plant, id }) => {
    return loading ? (
      <AppLoader size={'xl'} center={true} variant={'black'} p='xl' />
    ) : (
      <Box p='xl'>
        <Group align='flex-start' position='apart'>
          {/* Reference Section (left) */}
          <Group>
            <Stack spacing='sm'>
              <Group spacing='xs'>
                <Text fw={700} size='sm'>
                  Reference Group:
                </Text>
                <Text size='sm'>{refGroup}</Text>
              </Group>
              <Group spacing='xs'>
                <Text fw={700} size='sm'>
                  Reference Group Counter:
                </Text>
                <Text size='sm'>{refGroupCounter}</Text>
              </Group>
            </Stack>
          </Group>

          {/* Main Section (right) */}
          <Group align='flex-end' spacing='lg'>
            <Button
              variant='gradient'
              radius='md'
              size='sm'
              onClick={() => handleCopy(true)}
              disabled={!refGroup || btnDisable || isCancelled}
              style={{ width: 200 }}
            >
              Copy Group
            </Button>

            <Stack spacing={4}>
              <Text fw={700} size='sm'>
                Main Group :
              </Text>
              <TextInput
                size='sm'
                value={mainGroup}
                readOnly
                onChange={(e) => setMainGroup(e.currentTarget.value)}
              />
            </Stack>
            <Button
              variant='gradient'
              radius='md'
              size='sm'
              onClick={() => handleCopy(false)}
              disabled={!refGroup || btnDisable || isCancelled}
              style={{ width: 200 }}
            >
              Copy Group Counter
            </Button>

            <Stack spacing={4}>
              <Text fw={700} size='sm'>
                Main Group Counter :
              </Text>
              <TextInput
                size='sm'
                value={mainGroupCounter}
                readOnly
                onChange={(e) => setMainGroupCounter(e.currentTarget.value)}
              />
            </Stack>

            <Button
              color='dark'
              radius='md'
              onClick={() => handleUpdate({ producing_plant, id })}
              disabled={(!refGroup && !refGroupCounter) || btnDisable || isCancelled }
            >
              Update
            </Button>
          </Group>
        </Group>
        {/* <Box pt='xl' style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Checkbox
            checked={confirmed}
            onChange={(e) => setConfirmed(e.currentTarget.checked)}
            label='Material is assigned for the new recipe in ERP'
          />
        </Box> */}
      </Box>
    );
  };

  const Footer = (activePlant) => (
    <FooterContainer>
      <Button
        onClick={handleSubmit}
        variant='gradient'
        radius='md'
        size='md'
        fullWidth
        disabled={btnDisable || isCancelled || loading}
      >
        {loading ? <AppLoader size='sm' /> : 'Submit'}
      </Button>
    </FooterContainer>
  );
  return (
    <>
    {opened && (
        <AddChangeNumberPopup
          opened={opened}
          onClose={() => setOpened(false)}
          crNumber={crNumber}
          isBom={false}
        />
      )}
      <ContentPaper footer={<Footer activePlant={activePlant} />}>
        <ProjectOverview />
        <Space h='sm' />
        <Tabs
          keepMounted={false}
          value={activePlant}
          onTabChange={(value) => {
            setActivePlant(value);
            getRecipeGroup(value);
          }}
          color='yellow'
        >
          <Tabs.List>
            {producing_plant_data.map(({ producing_plant, id }, index) => (
              <Tabs.Tab value={producing_plant} className='menu-tabs'>
                {producing_plant}
              </Tabs.Tab>
            ))}
          </Tabs.List>
          {producing_plant_data.map(({ producing_plant, id }, index) => {
            return (
              <Tabs.Panel value={producing_plant}>
                <RecipeGroupCounter producing_plant={producing_plant} id={id} />
                <MantineTable
                  columns={columns}
                  initialData={baseSku
                    .map((sku) => ({
                      ...sku,
                      error: (errors?.[activePlant] || []).includes(sku.component_description),
                    }))
                    .filter(
                      (item) =>
                        (item?.producingPlant?.producing_plant === producing_plant ||
                          item?.pp_id === producing_plant) &&
                        (!switchState || (switchState && item.flagged)),
                    )}
                  enablePinning
                  initialState={initialState}
                  unique={'indexId'}
                  enableRowSelection={false}
                  loading={loading}
                  showSelectedToggle={false}
                  hideSelectColumn={true}
                  showResetAll={false}
                  enableExport={true}
                  getExportData={getExportData}
                  pageSize={10}
                  enableSelectAll={false}
                  rightButtons={rightSideBtns}
                  leftButtons={leftButtons}
                  headerBadge={<RefBadge crNumber={crNumber} activePlant={activePlant} />}
                  csvFilename={`${activePlant}_norms_adjustment_${moment().format('YYYY-MM-DD')}`}
                />
              </Tabs.Panel>
            );
          })}
        </Tabs>
      </ContentPaper>
      <NotificationModal
        notificationModalOpen={notificationModalOpen}
        setNotificationModalOpen={setNotificationModalOpen}
        notificationText={notificationPopup.text}
        title={notificationPopup.title}
      />
    </>
  );
};
